import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
  DEFAULT_LANGUAGE,
  PREFERRED_LANG_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
  isSupportedLanguage,
} from './language.constants';

const BROWSER_LOCALE: Record<SupportedLanguage, string> = {
  es: 'es-AR',
  en: 'en-US',
  pt: 'pt-BR',
};

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly translationRevision = signal(0);

  readonly languages = SUPPORTED_LANGUAGES;
  readonly activeLang = signal<SupportedLanguage>(DEFAULT_LANGUAGE);

  constructor() {
    this.setLanguage(this.initialLanguage(), false);
  }

  setLanguage(lang: SupportedLanguage, persist = true): void {
    this.transloco.load(lang).subscribe({
      next: () => {
        this.transloco.setActiveLang(lang);
        this.activeLang.set(lang);
        this.translationRevision.update((revision) => revision + 1);
        if (persist && typeof localStorage !== 'undefined') {
          localStorage.setItem(PREFERRED_LANG_STORAGE_KEY, lang);
        }
      },
    });
  }

  locale(): string {
    const lang = this.activeLang();
    return BROWSER_LOCALE[isSupportedLanguage(lang) ? lang : DEFAULT_LANGUAGE];
  }

  translate(key: string, params?: Record<string, unknown>): string {
    this.activeLang();
    this.translationRevision();
    return this.transloco.translate(key, params);
  }

  private initialLanguage(): SupportedLanguage {
    if (typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(PREFERRED_LANG_STORAGE_KEY);
      if (stored && isSupportedLanguage(stored)) return stored;
    }

    if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0];
      if (isSupportedLanguage(browserLang)) return browserLang;
    }

    return DEFAULT_LANGUAGE;
  }
}
